import { useCallback, useEffect } from "react";
import { useFormContext } from "react-hook-form";

import { getDefaultConsigneeValue } from "pages/shipper/RequestFormV2/defaults/consigneeDefaults";
import { ScheduleStopValue } from "pages/shipper/RequestFormV2/defaults/scheduleDefaults";
import { consigneeFieldPaths, getConsigneeFieldPath } from "@RequestFormV2/sections/Schedule/utils/consigneeFieldPaths";
import { DELIVERY_STOP_INDEX, mapDeliveryStopToConsignee } from "@RequestFormV2/sections/Schedule/utils/mapStopToConsignee";
import { usePersistDraft } from "@RequestFormV2/hooks/usePersistDraft";

export function useConsigneeSync() {
  const { getValues, setValue, watch } = useFormContext();
  const persistDraft = usePersistDraft();

  const sameAsDelivery = watch(consigneeFieldPaths.same_as_delivery_address_contact);
  const deliveryStop = watch(`schedule.stops.${DELIVERY_STOP_INDEX}`) as ScheduleStopValue | undefined;
  const deliveryStopKey = JSON.stringify(deliveryStop);

  const syncFromDelivery = useCallback(() => {
    const stop = getValues(`schedule.stops.${DELIVERY_STOP_INDEX}`) as ScheduleStopValue;
    if (!stop) return;

    const mapped = mapDeliveryStopToConsignee(stop);
    (Object.keys(mapped) as (keyof typeof mapped)[]).forEach((key) => {
      setValue(getConsigneeFieldPath(key), mapped[key]);
    });
  }, [getValues, setValue]);

  const clearConsigneeFields = useCallback(() => {
    const defaults = getDefaultConsigneeValue();
    const fieldsToReset: (keyof typeof defaults)[] = [
      "address_reference_number",
      "address_id",
      "address_name",
      "address_street",
      "address_street_line2",
      "address_city",
      "address_state",
      "address_country",
      "address_zipcode",
      "airport",
      "airport_name",
      "seaport",
      "seaport_name",
      "contact_name",
      "contact_phone",
      "contact_email",
    ];
    fieldsToReset.forEach((field) => {
      setValue(consigneeFieldPaths[field], defaults[field]);
    });
    setValue(consigneeFieldPaths.form_address_contact_type, "custom");
  }, [setValue]);

  const handleSameAsDeliveryChange = useCallback(
    (checked: boolean) => {
      setValue(consigneeFieldPaths.same_as_delivery_address_contact, checked);
      if (checked) {
        syncFromDelivery();
      } else {
        clearConsigneeFields();
      }
      persistDraft("schedule");
    },
    [setValue, syncFromDelivery, clearConsigneeFields, persistDraft],
  );

  useEffect(() => {
    if (sameAsDelivery) {
      syncFromDelivery();
    }
  }, [sameAsDelivery, deliveryStopKey, syncFromDelivery]);

  return {
    sameAsDelivery: Boolean(sameAsDelivery),
    fieldsDisabled: Boolean(sameAsDelivery),
    handleSameAsDeliveryChange,
  };
}

export type ConsigneeSyncSlice = ReturnType<typeof useConsigneeSync>;
